import { ControlContainer, NG_VALUE_ACCESSOR } from '@angular/forms';
import { Component, ElementRef, forwardRef, Host, Input, OnInit, Optional, SkipSelf, ViewChild } from '@angular/core';

import { AmountControl, BaseComponent, Helper } from '../../classes';

const MINUS_CHAR = '-';

@Component({
    selector: 'app-amount-input',
    templateUrl: './amount-input.component.html',
    styleUrls: ['./amount-input.component.scss'],
    providers: [{
        provide: NG_VALUE_ACCESSOR,
        useExisting: forwardRef(() => AmountInputComponent),
        multi: true
    }]
})
export class AmountInputComponent extends BaseComponent<string, string, AmountControl> implements OnInit {
    public currencyPlaceholder: string;
    public percentagePlaceholder: string;

    @ViewChild('input', { static: true }) inputReference: ElementRef;

    @Input() public disabled: boolean;
    @Input() public onlyIntegers: boolean;
    @Input() public allowNegative: boolean;
    @Input() public disableMoveCaretToEnd: boolean;

    constructor(
        @Optional() @Host() @SkipSelf()
        protected controlContainer: ControlContainer
    ) {
        super(controlContainer);
        this.currencyPlaceholder = 'Currency';
        this.percentagePlaceholder = 'Percentage';
    }

    private formatAmount(value: string, key = ''): string {
        const decimalSeparator = '.';
        const thousandsSeparator = ',';
        const valueParts = value.split(decimalSeparator);

        if (valueParts[0].length >= 5) {
            valueParts[0] = valueParts[0].split(thousandsSeparator).join('')
                .replace(...Helper.getFormatAmountRegex(thousandsSeparator));
        }

        value = valueParts[0];

        if (!this.onlyIntegers) {
            if (!valueParts[1]) {
                if (key === decimalSeparator) {
                    value += key;
                }
            } else {
                value += decimalSeparator + valueParts[1];
            }
        }

        return value;
    }

    private prepareElementValue(value: string | number): string {
        if (value !== 0 && !value) {
            return '';
        }
        const valueStr = String(value);
        if (!this.asFormsControl().isInitialFormattingActive) {
            return valueStr;
        }

        const nonFormattedAmount = typeof value === 'string' ? Helper.getNonFormattedAmount(valueStr) : valueStr;
        const  formattedPrice = this.checkNegativeValueAndGetFormattedPrice(nonFormattedAmount);

        return formattedPrice?.replace(/,/g, ' ');
    }

    private checkNegativeValueAndGetFormattedPrice(nonFormattedAmount: string): string {
        if (this.allowNegative && nonFormattedAmount.startsWith(MINUS_CHAR)) {
            const priceWithoutMinus = nonFormattedAmount.slice(1, nonFormattedAmount.length);

            return MINUS_CHAR + Helper.formatDecimal(+priceWithoutMinus, null, this.asFormsControl().fourDecimals ? 4 : 2);
        }

        return Helper.formatDecimal(+nonFormattedAmount, null, this.asFormsControl().fourDecimals ? 4 : 2);
    }

    ngOnInit() {
        this.asFormsControl();
        this.checkInvalid();
    }

    moveCaretToEnd(event): void {
        if (this.disableMoveCaretToEnd) {
            return;
        }

        const target = (event.target as HTMLInputElement);

        if (typeof target.selectionStart === 'number') {
            target.selectionStart = target.selectionEnd = target.value.length;
        }
    }

    convertToFloat(event): void {
        const target = event.target as HTMLInputElement;

        if (target.value.trim() !== '') {
            const valueParts = target.value.split('.');

            if (this.onlyIntegers) {
                this.changeValue(valueParts[0]);

                return;
            }

            const secondPartLength = !valueParts[1] ? 0 : valueParts[1].length;

            let value = '';

            if (!this.asFormsControl().fourDecimals) {
                switch (secondPartLength) {
                    case 0:
                        value = `${valueParts[0]}.00`;
                        break;
                    case 1:
                        value = `${valueParts[0]}.${valueParts[1]}0`;
                        break;
                    default:
                        value = target.value;
                }
            } else {
                switch (secondPartLength) {
                    case 0:
                        value = `${valueParts[0]}.0000`;
                        break;
                    case 1:
                        value = `${valueParts[0]}.${valueParts[1]}000`;
                        break;
                    case 2:
                        value = `${valueParts[0]}.${valueParts[1]}00`;
                        break;
                    case 3:
                        value = `${valueParts[0]}.${valueParts[1]}0`;
                        break;
                    default:
                        value = target.value;
                }
            }

            this.changeValue(value);
        } else {
            this.changeValue(null);
        }
    }

    onKeyPress(event): boolean {
        const target = event.target as HTMLInputElement;
        const selectionStart = target.selectionStart;
        const selectionEnd = target.selectionEnd;

        if ((selectionStart !== selectionEnd) && (target.value.length === selectionEnd)) {
            target.value = '';
        }

        const regex = Helper.getMatchAmountRegex(true);
        let value = target.value;
        let key = event.key;
        let isNegative = false;

        if (this.allowNegative) {
            if (key === MINUS_CHAR) {
                isNegative = true;
                key = '';
                value = value.replace(MINUS_CHAR, '');
            }
            else if (value.startsWith(MINUS_CHAR)) {
                isNegative = true;
                value = value.replace(MINUS_CHAR, '');
            }
        }

        value = value + key;

        const pointIndex = value.indexOf('.');
        const pointValuePosition = this.asFormsControl().fourDecimals ? value.length - 5 : value.length - 3;
        const pointIndexCheck = pointIndex >= 0 && (pointIndex < pointValuePosition);

        if (
            (!isNegative && !regex.test(Helper.getNonFormattedAmount(value)))
            ||
            (pointIndexCheck && !this.onlyIntegers)
        ) {
            event.preventDefault();

            return false;
        } else {
            value = this.formatAmount(value, key);
        }

        if (isNegative) {
            value = '-' + value;
        }

        this.onChange(value);
        this.checkInvalid();
        this.control.resetErrors();

        super.writeValue(value);

        target.value = value;

        event.preventDefault();
    }

    onPaste(event): void {
        const clipboardData = event.clipboardData;
        const regex = Helper.getMatchAmountRegex(this.asFormsControl().fourDecimals);

        let value = clipboardData.getData('text');

        setTimeout(() => {
            let isNegative = false;

            if (this.allowNegative && value.startsWith(MINUS_CHAR)) {
                isNegative = true;
                value = value.replace(MINUS_CHAR, '');
            }

            if (!regex.test(value)) {
                this.changeValue('');
            } else {
                if (isNegative) {
                    value = '-' + value;
                }
                value = this.formatAmount(value);
                this.changeValue(value);
            }
        }, 0);
    }

    changeValue(value: string): void {
        this.onChange(value);
        this.writeValue(value);
    }

    writeValue(value: string): void {
        this.inputReference.nativeElement.value = this.prepareElementValue(value);
        this.checkInvalid();
        this.control.resetErrors();

        super.writeValue(value);
    }

    asFormsControl(): AmountControl {
        if (!this.control) {
            this.control = new AmountControl({
                name: '',
                value: null,
                placeholder: '',
                validators: [],
                componentType: 0,
                invalid: false,
                errors: [],
                currency: 'USD',
                isPercentage: false,
                fourDecimals: false
            });
        }

        return this.control as AmountControl;
    }
}
